import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { FlagIcon } from 'lucide-react';
import { useState } from 'react';
import { DataTable, EmptyState, ErrorNotice, Panel, Pill, SearchInput, SegmentedControl, SkeletonTable, type PillTone } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { CAMPAIGN_STAGE, CAMPAIGN_STAGE_LABELS, listCampaigns, type CampaignStage, type StaffCampaignRow } from '@/lib/campaigns';

/**
 * A client's campaign inquiries, by stage, on the organization's Campaigns
 * tab. One initiative delivered as several events by the client's teams:
 * they send it, it is answered with a proposal and an agreement, and from
 * there it is planned and delivered.
 */

const CAMPAIGN_SORT_FIELDS = {
  campaign: 'name',
  stage: 'stage',
  events: 'events',
  group: 'combinedGroupSize',
  sent: 'submittedAt',
} as const;
const CAMPAIGN_SORT_KEYS = Object.values(CAMPAIGN_SORT_FIELDS);
const DEFAULT_SORT = { key: CAMPAIGN_SORT_FIELDS.sent, direction: 'desc' } as const;

const STAGE_TONES: Readonly<Record<CampaignStage, PillTone>> = {
  DRAFT: 'neutral',
  INQUIRY: 'warning',
  PROPOSAL: 'info',
  AGREEMENT: 'info',
  PLANNING: 'info',
  DELIVERY: 'brand',
  REPORTING: 'info',
  CLOSED: 'neutral',
};

export function stageTone(stage: CampaignStage): PillTone {
  return STAGE_TONES[stage];
}

/** The list filters: what needs approval now, what is in progress, everything. */
const FILTERS = {
  open: 'open',
  active: 'active',
  all: 'all',
} as const;
type Filter = (typeof FILTERS)[keyof typeof FILTERS];

const FILTER_STAGES: Readonly<Record<Filter, readonly CampaignStage[] | null>> = {
  open: [CAMPAIGN_STAGE.inquiry, CAMPAIGN_STAGE.proposal, CAMPAIGN_STAGE.agreement],
  active: [CAMPAIGN_STAGE.planning, CAMPAIGN_STAGE.delivery, CAMPAIGN_STAGE.reporting],
  all: null,
};

function shortDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function windowLabel(row: StaffCampaignRow): string {
  if (!row.windowStart) return 'Dates to come';
  const start = shortDate(row.windowStart);
  const end = row.windowEnd ? shortDate(row.windowEnd) : null;
  return end && end !== start ? `${start} to ${end}` : start;
}

/** The organization's campaigns, filtered by where they stand. */
export function CampaignsTable({ organizationId }: { readonly organizationId: string }): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const filter = (Object.values(FILTERS) as string[]).includes(params.get('show') ?? '') ? (params.get('show') as Filter) : FILTERS.open;
  const { sort, onSort } = useTableSort(CAMPAIGN_SORT_KEYS, DEFAULT_SORT);
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();

  const campaigns = useQuery({
    queryKey: ['campaigns', organizationId, filter === FILTERS.all],
    queryFn: ({ signal }) => listCampaigns({ organizationId, includeDrafts: filter === FILTERS.all }, signal),
  });

  const stages = FILTER_STAGES[filter];
  const rows = sortRows(
    (campaigns.data ?? [])
      .filter((row) => stages === null || stages.includes(row.stage))
      .filter((row) => needle.length === 0 || row.name.toLowerCase().includes(needle) || CAMPAIGN_STAGE_LABELS[row.stage].toLowerCase().includes(needle))
      .map((row) => ({ ...row })),
    sort,
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl
          label="Which campaigns to show"
          options={[
            { key: FILTERS.open, label: 'Needs approval' },
            { key: FILTERS.active, label: 'In progress' },
            { key: FILTERS.all, label: 'All' },
          ]}
          value={filter}
          onChange={(key) =>
            setParams((previous) => {
              const next = new URLSearchParams(previous);
              next.set('show', key);
              return next;
            })
          }
        />
        <SearchInput
          id="campaigns-search"
          label="Search campaigns"
          className="w-72"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search campaigns…"
        />
      </div>

      {campaigns.isPending ? (
        <SkeletonTable rows={4} columns={['w-[30%]', 'w-[20%]', 'w-[12%]', 'w-[10%]', 'w-[10%]', 'w-[14%]']} label="Loading campaigns…" />
      ) : campaigns.isError ? (
        <ErrorNotice title="Campaigns unavailable" message={errorMessage(campaigns.error)} />
      ) : rows.length === 0 && needle.length > 0 ? (
        <Panel>
          <EmptyState icon={<FlagIcon aria-hidden className="size-6" />} title="No matches" message={`No campaign matches “${search.trim()}”.`} />
        </Panel>
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<FlagIcon aria-hidden className="size-6" />}
            title={filter === FILTERS.open ? 'Nothing needs approval' : 'No campaigns here'}
            message={
              filter === FILTERS.open
                ? 'When the client sends a campaign inquiry, or a proposal or agreement is with them, it appears here.'
                : 'A campaign is one initiative delivered as several events. The client starts it on their portal.'
            }
          />
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Campaign', sortKey: CAMPAIGN_SORT_FIELDS.campaign },
            { label: 'Stage', sortKey: CAMPAIGN_SORT_FIELDS.stage },
            { label: 'Events', sortKey: CAMPAIGN_SORT_FIELDS.events },
            { label: 'Participants', sortKey: CAMPAIGN_SORT_FIELDS.group },
            { label: 'Submitted', sortKey: CAMPAIGN_SORT_FIELDS.sent },
          ]}
          rightAlignedHeaders={[2, 3]}
          sort={sort}
          onSort={onSort}
        >
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small">
                <Link to={`/campaigns/${row.id}`} className="font-bold text-primary underline-offset-2 hover:underline">
                  {row.name}
                </Link>
                <span className="block text-portal-micro text-portal-muted">{windowLabel(row)}</span>
              </td>
              <td className="p-3 text-portal-small whitespace-nowrap">
                <Pill tone={stageTone(row.stage)}>{CAMPAIGN_STAGE_LABELS[row.stage]}</Pill>
              </td>
              <td className="p-3 text-portal-small text-right whitespace-nowrap">
                {row.events + row.inquiries}
                {row.inquiries > 0 ? (
                  <Pill tone="warning" className="ml-2">
                    {row.events} approved
                  </Pill>
                ) : null}
              </td>
              <td className="p-3 text-portal-small text-right">{row.combinedGroupSize > 0 ? row.combinedGroupSize.toLocaleString('en-US') : '—'}</td>
              <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{shortDate(row.submittedAt)}</td>
            </tr>
          ))}
        </DataTable>
      )}
    </div>
  );
}
